/**
 * Office.js capability detection for graceful degradation across Office
 * versions.
 *
 * The manifest declares a floor of WordApi 1.1 (Office 2016+), so the add-in
 * LOADS on every desktop Word — including perpetual/volume Office 2016/2019/2021,
 * which top out at WordApi 1.3. Several document-editing features need higher
 * requirement sets that those versions lack, so we feature-detect them at
 * runtime here rather than hard-gating the whole add-in in the manifest.
 *
 *   WordApi 1.4 — `Document.changeTrackingMode`. Used for the "force track
 *                 changes off during apply" safeguard (ooxmlRedline) and for
 *                 recording quote-edits as native tracked changes.
 *   WordApi 1.6 — `Body.getTrackedChanges()` + `TrackedChange.accept/reject`.
 *                 Powers the in-pane Accept/Reject review cards.
 *
 * First perpetual Office with each set: 1.4 → Office 2024, 1.6 → Office 2024
 * (both available on any Microsoft 365 subscription). See the WordApi
 * requirement-sets table:
 * https://learn.microsoft.com/en-us/javascript/api/requirement-sets/word/word-api-requirement-sets
 *
 * Below the required set, editing still works: the AI's redline is baked into
 * the DOCX as real Word `w:ins`/`w:del` revisions, so edits apply as native
 * tracked changes and the user reviews them via Word's Review ribbon instead
 * of our in-pane cards.
 */

function isSetSupported(set: string, version: string): boolean {
  try {
    return (
      typeof Office !== 'undefined' &&
      !!Office.context?.requirements?.isSetSupported &&
      Office.context.requirements.isSetSupported(set, version)
    );
  } catch {
    // isSetSupported can throw if Office isn't fully initialised yet; treat
    // any failure as "unsupported" so callers take the safe fallback path.
    return false;
  }
}

/**
 * WordApi 1.4 — programmatic read/write of the document's track-changes mode
 * (`Document.changeTrackingMode`). False on perpetual Office below 2024.
 */
export function supportsTrackControl(): boolean {
  return isSetSupported('WordApi', '1.4');
}

/**
 * WordApi 1.6 — enumerating and accepting/rejecting individual tracked
 * changes (`Body.getTrackedChanges()`). Gates the in-pane Accept/Reject review
 * cards. False on perpetual Office below 2024.
 */
export function supportsTrackedChangeReview(): boolean {
  return isSetSupported('WordApi', '1.6');
}
